import {
  getVariableValue,
  stringifyVariableValue,
} from "@/shared/utils/resolve-tokens";
import {
  isDateVariableName,
  isNowBasedDateName,
  parseDateToken,
} from "@features/dynamic-variables/date-variable";
import {
  type RepeatableTreeNode,
  repeatablePillTarget,
} from "@features/dynamic-variables/repeatable/scope";
import {
  VARIABLE_BLOCK_TOKEN_PATTERN,
  createBlockElement,
} from "@/shared/utils/variable-block";
import {
  VARIABLE_TOKEN_PATTERN,
  createPillElement,
  type PillDecorator,
} from "@/shared/utils/variable-pill";
import type { Node as PmNode, NodeType } from "prosemirror-model";
import { type Command, NodeSelection, Plugin } from "prosemirror-state";
import type { Transform } from "prosemirror-transform";
import type {
  EditorProps,
  EditorView,
  NodeViewConstructor,
} from "prosemirror-view";
import { textSchema } from "./schema";

const { nodes } = textSchema;

/** Pills and version-mode blocks — the non-editable variable atoms. */
const isVariableAtom = (node: PmNode | null | undefined) =>
  node?.type === nodes.variable || node?.type === nodes.variable_block;

/**
 * Replace every `pattern` match with a `type` atom named by its first group.
 * Scans each run of adjacent text nodes as one string, so a token that
 * formatting split (`{{first<strong>Name}}</strong>`) still becomes one atom —
 * the case Froala needed repairSplitVariableTokens for. The atom takes the
 * marks of the token's first character.
 */
const replaceTokens = <T extends Transform>(
  tr: T,
  pattern: string,
  type: NodeType,
): T => {
  const tokens: Array<{ from: number; to: number; name: string }> = [];
  tr.doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    if (!node.textContent.includes("{{")) return false;
    let run = "";
    let runStart = 0;
    const scan = () => {
      const re = new RegExp(pattern, "g");
      for (let m = re.exec(run); m; m = re.exec(run)) {
        const from = runStart + m.index;
        tokens.push({ from, to: from + m[0].length, name: m[1] });
      }
      run = "";
    };
    node.forEach((child, offset) => {
      if (!child.isText) return scan();
      if (!run) runStart = pos + 1 + offset;
      run += child.text;
    });
    scan();
    return false;
  });
  // Back to front: replacing a token never shifts the ones before it.
  for (const { from, to, name } of tokens.reverse()) {
    const marks = tr.doc.nodeAt(from)?.marks;
    tr.replaceWith(from, to, type.create({ name }, null, marks));
  }
  return tr;
};

/** `{{name}}` → pill. `{{{blocks}}}` are left alone (see applyVariableBlocks). */
export const applyVariableTokens = <T extends Transform>(tr: T): T =>
  replaceTokens(tr, VARIABLE_TOKEN_PATTERN, nodes.variable);

/**
 * Version mode only: `{{{name}}}` → a read-only block showing the variable's
 * HTML value. Authoring keeps the token as editable text, as Froala does.
 */
export const applyVariableBlocks = <T extends Transform>(tr: T): T =>
  replaceTokens(tr, VARIABLE_BLOCK_TOKEN_PATTERN, nodes.variable_block);

/**
 * Backspace (-1) / Delete (1) next to a pill or block removes it whole. With no binding
 * the key falls to the browser's native deletion of a non-editable inline
 * element, which is exactly what Froala needed pill-atomic.ts to paper over.
 */
export const deletePill =
  (dir: -1 | 1): Command =>
  (state, dispatch) => {
    const { $from, empty } = state.selection;
    const pill = dir < 0 ? $from.nodeBefore : $from.nodeAfter;
    if (!empty || !pill || !isVariableAtom(pill)) return false;
    if (dispatch) {
      const from = dir < 0 ? $from.pos - pill.nodeSize : $from.pos;
      dispatch(state.tr.delete(from, from + pill.nodeSize).scrollIntoView());
    }
    return true;
  };

/**
 * Turn tokens into atoms as they appear — typed, pasted, inserted by the
 * toolbar or AI. `blocks` (version mode) also converts `{{{name}}}`.
 */
export const variableTokensPlugin = ({ blocks = false } = {}) =>
  new Plugin({
    appendTransaction(transactions, _old, state) {
      if (!transactions.some((tr) => tr.docChanged)) return null;
      const tr = applyVariableTokens(state.tr);
      if (blocks) applyVariableBlocks(tr);
      return tr.docChanged ? tr : null;
    },
  });

export type PillContext = {
  /** Version data (version mode) — the pill shows the value; else its token. */
  data: Record<string, unknown> | undefined;
  /** Calc / date / repeatable look, shared with Froala and the inactive render. */
  decorate: PillDecorator;
  isVersion: boolean;
  /** User-created date variables — dates by definition, not by name. */
  customDateNames: ReadonlySet<string>;
  calcFormulas: Record<string, string>;
  repeatableRoots: RepeatableTreeNode[];
};

/** What clicking an already-selected pill opens. */
export type PillPopover =
  /** Authoring: the date token's format / offset. */
  | { kind: "date"; token: string }
  /** Version: the sender's value for this variable. */
  | { kind: "value"; name: string; valueType: "text" | "date" }
  /** Version: a repeatable field edits rows, in the rows dialog. */
  | { kind: "repeatable"; name: string };

/**
 * Same decision Froala's classifyPillTarget makes. Null = nothing to edit
 * (a template variable, a calculation, a built-in "today"-based date): the
 * click only selects the pill.
 */
export const pillPopoverFor = (
  name: string,
  ctx: PillContext,
): PillPopover | null => {
  const bareName = parseDateToken(name).name;
  if (isDateVariableName(bareName, ctx.customDateNames)) {
    if (!ctx.isVersion) return { kind: "date", token: name };
    if (isNowBasedDateName(bareName)) return null;
    return { kind: "value", name: bareName, valueType: "date" };
  }
  if (!ctx.isVersion || ctx.calcFormulas[name]) return null;
  if (repeatablePillTarget(name, ctx.repeatableRoots)) {
    return { kind: "repeatable", name };
  }
  return { kind: "value", name, valueType: "text" };
};

/**
 * Draws the pill with the same `createPillElement` Froala and the inactive
 * render use, so all three look identical. Label refreshes after version data
 * changes go through refreshPillValues on the live DOM — those mutations are
 * ours, not edits, so ProseMirror ignores them.
 */
export const variablePillView =
  (getContext: () => PillContext): NodeViewConstructor =>
  (node) => {
    const { data, decorate } = getContext();
    const name = node.attrs.name as string;
    const resolved = stringifyVariableValue(getVariableValue(data, name));
    return {
      dom: createPillElement(document, name, resolved, decorate),
      ignoreMutation: () => true,
    };
  };

/**
 * The version-mode block: Froala's createBlockElement — the value sanitized
 * with DOMPurify, the name as a badge (Richtext.scss). Refreshed in place by
 * refreshBlockValues when version data changes; those mutations aren't edits.
 */
export const variableBlockView =
  (getContext: () => PillContext): NodeViewConstructor =>
  (node) => {
    const name = node.attrs.name as string;
    const raw = getVariableValue(getContext().data, name);
    return {
      dom: createBlockElement(document, name, raw == null ? "" : String(raw)),
      ignoreMutation: () => true,
    };
  };

/**
 * Froala's two-click model (Figma 90:45304): the first click selects the pill
 * (ProseMirror's own atom selection), a click on the already-selected pill
 * calls `open`, which reports whether it opened something.
 */
export const clickSelectedPill =
  (
    open: (view: EditorView, pos: number) => boolean,
  ): NonNullable<EditorProps["handleClickOn"]> =>
  (view, _pos, node, nodePos, _event, direct) => {
    if (!direct || node.type !== nodes.variable) return false;
    const { selection } = view.state;
    const isSelected =
      selection instanceof NodeSelection && selection.from === nodePos;
    return isSelected && open(view, nodePos);
  };

/** Rename the pill at `pos` (a date's new format / offset) — a normal, undoable edit. */
export const setPillName =
  (pos: number, name: string): Command =>
  (state, dispatch) => {
    const node = state.doc.nodeAt(pos);
    if (node?.type !== nodes.variable || node.attrs.name === name) return false;
    dispatch?.(state.tr.setNodeMarkup(pos, undefined, { name }));
    return true;
  };

/**
 * Insert a variable token — the one primitive behind every insert path: the
 * toolbar picker, the `{{` typeahead and a drop onto the editor. It goes in as
 * text and variableTokensPlugin turns it into a pill with the marks at the
 * insert point; anything that isn't a `{{token}}` (a `{{{block}}}`) stays text.
 * A selected pill is kept and the new one lands after it — replacing it would
 * silently drop a variable the user never meant to remove.
 */
export const insertVariableToken =
  (token: string, range?: { from: number; to: number }): Command =>
  (state, dispatch) => {
    if (!token) return false;
    const { selection } = state;
    const keepPill =
      selection instanceof NodeSelection && isVariableAtom(selection.node);
    const { from, to } =
      range ??
      (keepPill ? { from: selection.to, to: selection.to } : selection);
    dispatch?.(state.tr.insertText(token, from, to).scrollIntoView());
    return true;
  };

/** Whether the `{{` that opened the typeahead still sits right before `pos`. */
export const hasTypeaheadTrigger = (
  doc: EditorView["state"]["doc"],
  pos: number,
) =>
  pos >= 2 && pos <= doc.content.size && doc.textBetween(pos - 2, pos) === "{{";
